/**
 * ProjectPage — one shared workspace.
 *
 * The header answers "where am I and who is here" on every tab, because in a
 * cross-organization workspace that context has to be permanent, not something
 * you go and look up. Everything else is four tabs: the work
 * (Conversations), the standing instructions (Context), the shared documents
 * (Files), and the people (Members).
 *
 * Destructive lifecycle is split deliberately. **Close** archives: access is
 * revoked, the project drops off members' lists, and the creator can reopen it.
 * **Delete** is permanent, creator-only, and requires typing the project name —
 * enforced on the server, not just here — because it destroys work contributed
 * by people in other organizations.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import type { Bot } from '@/types/models';
import projectService from '../services/projectService';
import type {
  ProjectAgent,
  ProjectMemberRow,
  ProjectSummary,
} from '../types';
import { PersonAvatarStack } from '../components/PersonAvatar';
import { ProjectGlyph, resolveProjectColor } from '../components/ProjectIdentity';
import { ProjectFormModal, type ProjectFormValues } from '../components/ProjectFormModal';
import { ShareProjectModal } from '../components/ShareProjectModal';
import { NewConversationModal } from '../components/NewConversationModal';
import { ProjectConversationsTab } from '../components/ProjectConversationsTab';
import { ProjectContextTab } from '../components/ProjectContextTab';
import { ProjectFilesTab } from '../components/ProjectFilesTab';
import { ProjectMembersTab } from '../components/ProjectMembersTab';
import { DeniedBlock, ErrorBlock } from '../components/StateBlocks';
import './ProjectPage.css';

type TabKey = 'conversations' | 'context' | 'files' | 'members';

export const ProjectPage: React.FC = () => {
  const { t } = useLanguage();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();

  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [agents, setAgents] = useState<ProjectAgent[]>([]);
  const [loading, setLoading] = useState(true);
  /** 'denied' is distinct from a transport error — it has its own surface. */
  const [failure, setFailure] = useState<'denied' | 'error' | null>(null);
  const [tab, setTab] = useState<TabKey>('conversations');

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conversationsToken, setConversationsToken] = useState(0);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    setFailure(null);
    Promise.all([
      projectService.getProject(projectId),
      projectService.listMembers(projectId),
      projectService.listAgents(projectId),
    ])
      .then(([loadedProject, loadedMembers, loadedAgents]) => {
        setProject(loadedProject);
        setMembers(loadedMembers);
        setAgents(loadedAgents);
      })
      .catch(err => {
        // The API answers 404 for both "no such project" and "not yours",
        // deliberately, so a non-member cannot probe which ids exist.
        setFailure(err?.response?.status === 404 || err?.response?.status === 403
          ? 'denied'
          : 'error');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSaveProject = async (values: Partial<ProjectFormValues>) => {
    const updated = await projectService.updateProject(projectId, values);
    setProject(updated);
  };

  const handleClose = async () => {
    setBusy(true);
    setActionError(null);
    try {
      setProject(await projectService.archiveProject(projectId));
      setCloseOpen(false);
    } catch (err: any) {
      setActionError(err?.response?.data?.error
        || (t('projects.page.closeFailed') || 'Could not close the project.'));
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    setActionError(null);
    try {
      setProject(await projectService.reopenProject(projectId));
    } catch (err: any) {
      setActionError(err?.response?.data?.error
        || (t('projects.page.reopenFailed') || 'Could not reopen the project.'));
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await projectService.leaveProject(projectId);
      navigate(tenantPath('/projects'), { replace: true });
    } catch (err: any) {
      setActionError(err?.response?.data?.error
        || (t('projects.page.leaveFailed') || 'Could not leave the project.'));
      setBusy(false);
      setLeaveOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!project || deleteConfirmName.trim() !== project.project_name) return;
    setBusy(true);
    setActionError(null);
    try {
      await projectService.deleteProject(projectId, deleteConfirmName.trim());
      navigate(tenantPath('/projects'), { replace: true });
    } catch (err: any) {
      setActionError(err?.response?.data?.error
        || (t('projects.page.deleteFailed') || 'Could not delete the project.'));
      setBusy(false);
    }
  };

  const handleStartConversation = (bot: Bot, threadMode: 'group' | 'solo') => {
    setNewConvOpen(false);
    // The thread is created lazily by the chat panel on the first send, with the
    // project stamped on it — so an abandoned picker never leaves an empty
    // conversation littering the project.
    // `mode` rides the URL because the thread does not exist yet — it is created
    // on the first send, so the choice has to survive until then. Only the
    // non-default is carried; a group thread needs no marker.
    const mode = threadMode === 'solo' ? '&mode=solo' : '';
    navigate(tenantPath(`/chat?bot=${bot.chatbot_id}&project=${projectId}${mode}`));
    setConversationsToken(n => n + 1);
  };

  if (loading) {
    // The placeholder wears the page's own CHROME. It used to be two stacks of
    // bars with no breadcrumb, hero or tab bar, so everything sat at the very
    // top of the page and then jumped down the moment the project arrived and
    // the real chrome pushed it into place. Same elements, same heights, only
    // the contents are grey — so nothing moves when the data lands.
    return (
      <div className="admin-container">
        <Sidebar />
        <div className="main-container">
          <main className="main-content project-page">
            <nav className="project-breadcrumb">
              <button type="button" className="pj-btn pj-btn-ghost pj-btn-sm" disabled>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="project-back-icon">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                {t('projects.title') || 'Projects'}
              </button>
            </nav>

            <header className="project-hero" aria-busy="true">
              <span className="project-hero-icon project-skel-block" aria-hidden="true" />
              <div className="project-hero-text">
                <div className="project-hero-titleline">
                  <h1 className="project-hero-title">
                    <span className="project-skel-bar is-title" />
                  </h1>
                </div>
                {/* No description line: the real hero only renders one when
                    the project HAS a description, and guessing wrong shifts the
                    tab bar by a whole line. Title and owner are always present,
                    so matching just those is exact for a project without a
                    description and one line short for one with it — the error
                    that costs least. */}
                <p className="project-hero-owner">
                  <span className="project-skel-bar is-owner" />
                </p>
              </div>
              <div className="project-hero-side">
                <span className="project-skel-pill" />
              </div>
            </header>

            <div className="project-tabs" role="tablist" aria-busy="true">
              {/* The REAL labels, painted over. A grey bar of a guessed height
                  left the tab bar 8px short and slid the whole body up; text
                  sets its own line box, so this matches to the pixel. */}
              {[
                t('projects.tabs.conversations') || 'Conversations',
                t('projects.tabs.context') || 'Context',
                t('projects.tabs.files') || 'Files',
              ].map(label => (
                <button
                  type="button"
                  className="project-tab project-tab-skeleton"
                  key={label}
                  disabled
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="project-tab-body">
              {/* Conversations is the tab that opens, so the placeholder wears
                  ITS shape: the filter toolbar, then rows built from the real
                  .conv-row box. Skipping the toolbar left every row 60px high
                  and they slid down as the real one appeared. */}
              <div className="pj-tab">
                <div className="conv-toolbar" aria-busy="true">
                  <div className="conv-filters">
                    <span className="project-skel-pill is-filter" />
                    <span className="project-skel-pill is-filter" />
                    <span className="project-skel-pill is-filter" />
                  </div>
                  <span className="project-skel-pill is-action" />
                </div>
                <ul className="conv-list">
                  {[0, 1, 2].map(i => (
                    <li key={i}>
                      <div className="conv-row">
                        <span className="project-skel-block is-conv-icon" />
                        <span className="conv-skel-text">
                          <span className="project-skel-bar is-conv-title" />
                          <span className="project-skel-bar is-conv-meta" />
                        </span>
                        <span className="project-skel-pill is-people" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (failure === 'denied' || !project) {
    return (
      <div className="admin-container">
        <Sidebar />
        <div className="main-container">
          <main className="main-content project-page">
            <DeniedBlock onBack={() => navigate(tenantPath('/projects'))} />
          </main>
        </div>
      </div>
    );
  }

  if (failure === 'error') {
    return (
      <div className="admin-container">
        <Sidebar />
        <div className="main-container">
          <main className="main-content project-page">
            <ErrorBlock
              message={t('projects.page.loadFailed') || 'Could not load this project.'}
              onRetry={load}
            />
          </main>
        </div>
      </div>
    );
  }

  const people = [
    {
      id: project.created_by_user_id,
      name: project.created_by_name,
      email: project.created_by_email,
      tenantName: project.owner_tenant_name,
    },
    ...members
      .filter(m => m.status === 'accepted')
      .map(m => ({
        id: m.member_user_id || m.member_tenant_id,
        name: m.member_name,
        email: m.member_email,
        tenantName: m.member_tenant_name,
      })),
  ];

  // Context and Files are the managers' surfaces: they DEFINE the project —
  // the brief its agents receive, the files they read. A member experiences
  // both through the conversations, so for them the workspace IS the
  // conversations, and the Members board stays visible so they can see who
  // they are working with. The backend refuses the hidden tabs' data to
  // non-managers regardless; hiding them here just stops the UI offering
  // doors that are locked.
  const tabs: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: 'conversations', label: t('projects.tabs.conversations') || 'Conversations' },
    ...(project.can_manage ? [
      { key: 'context' as TabKey, label: t('projects.tabs.context') || 'Context' },
      { key: 'files' as TabKey, label: t('projects.tabs.files') || 'Files' },
    ] : []),
    { key: 'members', label: t('projects.tabs.members') || 'Members', count: members.length + 1 },
  ];

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">
        {/* The project's colour washes its whole page, not just its card, so
            moving between projects feels like moving between places. Applied
            as one custom property; every tint below derives from it. */}
        <main
          className="main-content project-page is-themed"
          style={{ ['--project-accent' as string]: resolveProjectColor(project.project_color) }}
        >
          <nav className="project-breadcrumb">
            <button
              type="button"
              className="pj-btn pj-btn-ghost pj-btn-sm"
              onClick={() => navigate(tenantPath('/projects'))}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="project-back-icon">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {t('projects.title') || 'Projects'}
            </button>
          </nav>

          <header className="project-hero">
            <span className="project-hero-icon" aria-hidden="true">
              <ProjectGlyph icon={project.project_icon} strokeWidth={1.7} />
            </span>
            <div className="project-hero-text">
              <div className="project-hero-titleline">
                <h1 className="project-hero-title" dir="auto">{project.project_name}</h1>
                {project.is_archived && (
                  <span className="pj-pill pj-pill-neutral">
                    {t('projects.list.closed') || 'Closed'}
                  </span>
                )}
              </div>
              {project.project_description && (
                <p className="project-hero-desc" dir="auto">{project.project_description}</p>
              )}
              <p className="project-hero-owner">
                {(t('projects.page.ownedBy') || 'Created by {{name}} · {{org}}')
                  .replace('{{name}}', project.created_by_name || '')
                  .replace('{{org}}', project.owner_tenant_name || '')}
              </p>
            </div>

            <div className="project-hero-side">
              <PersonAvatarStack people={people} max={5} size={30} />
              {project.can_manage && !project.is_archived && (
                <button
                  type="button"
                  className="pj-btn pj-btn-primary"
                  onClick={() => setShareOpen(true)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <path d="M8.6 13.5 15.4 17.5M15.4 6.5 8.6 10.5" />
                  </svg>
                  {t('projects.page.share') || 'Share'}
                </button>
              )}
              {/* Every member gets the menu now: a contributor's only entry
                  is Leave, and hiding it behind manager rights meant there was
                  no way out of a project you were invited to. */}
              <ProjectMenu
                project={project}
                onEdit={() => setEditOpen(true)}
                onClose={() => setCloseOpen(true)}
                onReopen={handleReopen}
                onDelete={() => { setDeleteConfirmName(''); setDeleteOpen(true); }}
                onLeave={() => setLeaveOpen(true)}
                busy={busy}
              />
            </div>
          </header>

          {project.is_archived && (
            <div className="pj-notice pj-notice-warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="4" rx="1" />
                <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
              </svg>
              <div>
                {project.is_creator
                  ? (t('projects.page.archivedOwner')
                    || 'This project is closed. Members can no longer open it. Reopen it to continue working.')
                  : (t('projects.page.archivedMember')
                    || 'This project has been closed by its owner.')}
              </div>
            </div>
          )}

          {actionError && <div className="pj-form-error">{actionError}</div>}

          <div className="project-tabs" role="tablist">
            {tabs.map(item => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={tab === item.key}
                className={`project-tab ${tab === item.key ? 'is-active' : ''}`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
                {typeof item.count === 'number' && (
                  <span className="project-tab-count">{item.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="project-tab-body" role="tabpanel">
            {tab === 'conversations' && (
              <ProjectConversationsTab
                project={project}
                agents={agents}
                members={members}
                onNewConversation={() => setNewConvOpen(true)}
                refreshToken={conversationsToken}
              />
            )}
            {tab === 'context' && project.can_manage && (
              <ProjectContextTab project={project} onSave={handleSaveProject} />
            )}
            {tab === 'files' && project.can_manage && <ProjectFilesTab project={project} />}
            {tab === 'members' && (
              <ProjectMembersTab
                project={project}
                members={members}
                onMembersChanged={setMembers}
                onInvite={() => setShareOpen(true)}
              />
            )}
          </div>
        </main>
      </div>

      <ProjectFormModal
        isOpen={editOpen}
        project={project}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSaveProject}
      />

      <ShareProjectModal
        isOpen={shareOpen}
        project={project}
        agents={agents}
        onClose={() => setShareOpen(false)}
        onInvited={() => projectService.listMembers(projectId).then(setMembers)}
      />

      <NewConversationModal
        isOpen={newConvOpen}
        projectAgents={agents}
        members={members}
        projectTenantId={project.tenant_id}
        onClose={() => setNewConvOpen(false)}
        onStart={handleStartConversation}
      />

      <ConfirmModal
        isOpen={closeOpen}
        onClose={() => setCloseOpen(false)}
        onConfirm={handleClose}
        title={t('projects.page.closeTitle') || 'Close this project?'}
        message={t('projects.page.closeBody')
          || 'Members lose access and it disappears from their lists. Nothing is deleted, and you can reopen it at any time.'}
        confirmText={t('projects.page.close') || 'Close project'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="warning"
        loading={busy}
      />

      <ConfirmModal
        isOpen={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={handleLeave}
        title={t('projects.page.leaveTitle') || 'Leave this project?'}
        message={t('projects.page.leaveBody')
          || 'You will stop seeing it in your list, and lose access to its conversations. Anything you contributed stays. A manager can invite you back.'}
        confirmText={t('projects.page.leave') || 'Leave project'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="warning"
        loading={busy}
      />

      {deleteOpen && (
        <DeleteProjectDialog
          project={project}
          value={deleteConfirmName}
          onChange={setDeleteConfirmName}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
          busy={busy}
        />
      )}
    </div>
  );
};

/** Overflow menu for the lifecycle actions that must not sit in the hero row. */
function ProjectMenu({
  project,
  onEdit,
  onClose,
  onReopen,
  onDelete,
  onLeave,
  busy,
}: {
  project: ProjectSummary;
  onEdit: () => void;
  onClose: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onLeave: () => void;
  busy: boolean;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="project-menu" ref={ref}>
      <button
        type="button"
        className="pj-icon-btn"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('projects.page.moreActions') || 'More actions'}
        disabled={busy}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>
      {open && (
        <div className="project-menu-panel" role="menu">
          {!project.can_manage && !project.is_creator && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onLeave(); }}>
              {t('projects.page.leave') || 'Leave project'}
            </button>
          )}
          {project.can_manage && !project.is_archived && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onEdit(); }}>
              {t('projects.page.edit') || 'Edit project'}
            </button>
          )}
          {project.can_manage && (project.is_archived ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onReopen(); }}>
              {t('projects.page.reopen') || 'Reopen project'}
            </button>
          ) : (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onClose(); }}>
              {t('projects.page.close') || 'Close project'}
            </button>
          ))}
          {/* A manager who is not the creator can also walk away. */}
          {project.can_manage && !project.is_creator && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onLeave(); }}>
              {t('projects.page.leave') || 'Leave project'}
            </button>
          )}
          {/* Permanent deletion stays with the creator: a promoted manager can
              close a project, but destroying other tenants' work is the owner's
              decision alone. Enforced server-side too. */}
          {project.is_creator && (
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              onClick={() => { setOpen(false); onDelete(); }}
            >
              {t('projects.page.delete') || 'Delete permanently'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Type-the-name confirmation. The same check runs on the server. */
function DeleteProjectDialog({
  project,
  value,
  onChange,
  onCancel,
  onConfirm,
  busy,
}: {
  project: ProjectSummary;
  value: string;
  onChange: (next: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const { t } = useLanguage();
  const matches = value.trim() === project.project_name;

  return (
    <div className="pj-overlay" onClick={() => !busy && onCancel()} role="presentation">
      <div
        className="pj-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('projects.page.deleteTitle') || 'Delete project permanently'}
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 460 }}
      >
        <header className="pj-modal-header">
          <div className="pj-modal-heading">
            <h2 className="pj-modal-title">
              {t('projects.page.deleteTitle') || 'Delete project permanently'}
            </h2>
          </div>
        </header>
        <div className="pj-modal-body">
          <div className="pj-notice pj-notice-danger">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <div>
              {t('projects.page.deleteBody')
                || 'This deletes every conversation in the project, including work contributed by people in other organizations. It cannot be undone.'}
            </div>
          </div>
          <div className="pj-field">
            <label className="pj-label" htmlFor="pj-delete-confirm">
              {(t('projects.page.deleteConfirmLabel') || 'Type “{{name}}” to confirm')
                .replace('{{name}}', project.project_name)}
            </label>
            <input dir="auto"
              id="pj-delete-confirm"
              className="pj-input"
              value={value}
              onChange={e => onChange(e.target.value)}
              autoFocus
            />
          </div>
        </div>
        <footer className="pj-modal-footer">
          <button type="button" className="pj-btn pj-btn-secondary" onClick={onCancel} disabled={busy}>
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            className="pj-btn pj-btn-danger"
            onClick={onConfirm}
            disabled={!matches || busy}
          >
            {busy
              ? (t('common.saving') || 'Saving…')
              : (t('projects.page.deleteConfirm') || 'Delete forever')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ProjectPage;
